import reverse from 'lodash/reverse';

import {
  DonationPageFirstFormResponseType,
  DonationPageThirdFormResponseType,
  DonationPageSecondFormResponseType,
} from 'src/models/DonationPage/Section';
import {
  DonationPageMainSectionStoreType,
  DonationPageOptionsSectionStoreType,
  DonationPageCheckoutSectionStoreType,
} from 'src/models/DonationPage/Store';
import { getCurrencySymbolById } from 'src/utils/currency';
import { SortDirectionEnum } from 'src/utils/types';

/** options section */
export const loadDonationPageThirdFormData = (
  data: DonationPageThirdFormResponseType['CurrentSection'],
  currencyTypeID: number,
  currencyType?: string
): DonationPageOptionsSectionStoreType => ({
  terminology: {
    value: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.ID,
    options: data.TerminologiesViewModel.SupporterTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    plural: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Singular,
    article:
      data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
  },
  contribution: {
    options: data.TerminologiesViewModel.ContributionTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    value: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.ID,
    article:
      data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
    verb: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Verb,
    plural: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Singular,
  },
  currencySelected: {
    id: currencyTypeID,
    shortName: currencyType || getCurrencySymbolById(currencyTypeID) || '|',
  },
  FundCode: data.FundCode,
  hasFundCode: !!data.FundCode,
  showOrganizerPromoCode: data.PricingViewModel.ShowOrganizerPromoCode,
  OrganizerPromoCode: data.PricingViewModel.OrganizerPromoCode,
  organizerPromoCodeString: data.PricingViewModel.OrganizerPromoCodeString,
  UkGiftAidShown: data.UkGiftAidShown,
  UkGiftAidEnabled: data.IsUkGiftAidEnabled,
  giftAidText: data.GiftAidStatement,
  giftAidAgreementText: data.GiftAidAgreement,
  ContributionBankStatement: data.ContributionBankStatement,
  showAdditionalAmount: data.ShowAdditionalAmount,
  canHaveAdditionalAmount: data.CanHaveAdditionalAmount,
  isPledge: data.IsPledge,
  areFixedContributionsAllowed: data.AreFixedContributionsAllowed,
  areFixedContributionsAllowedVisible: data.AreFixedContributionsAllowedVisible,
  isDonationWithoutItemsHeadlineAndButtonVisible: data.IsDonationWithoutItemsHeadlineAndButtonVisible,
  donationWithoutItemsHeadLineText: data.DonationWithoutItemsHeadLineText,
  donateWithoutItemsButtonText: data.DonateWithoutItemsButtonText,
  showAreCompleteAnonymousContributions: data.ShowAreCompleteAnonymousContributions,
  areCompleteAnonymousContributionsEnabled: !data.AreCompleteAnonymousContributionsDisabled,
  allowDonationDedication: data.AllowDonationDedication,
  showSpecialInstructions: data.ShowSpecialInstructions,
  isSpecialInstructionsVisible: data.IsSpecialInstructionsVisible,
  useCustomMinMaxContributionAmount: data.UseCustomMinMaxContributionAmount,
  minimumContributionAmount: data.MinimumContributionAmount,
  maximumContributionAmount: data.MaximumContributionAmount,
  enableMarketingEmailsOption: data.EnableMarketingEmailsOption,
  EnableMarketingEmailsOptionLabel: data.EnableMarketingEmailsOptionLabel,
  notice: data.Notice,
  isSupporterNoticeAcknowledgmentRequired: data.IsSupporterNoticeAcknowledgmentRequired,
  ContributionNotificationRecipients: data.ContributionNotificationRecipients
    ? data.ContributionNotificationRecipients.map(({ Email }) => Email)
    : [],
  ContactOrganizerEmailRecipients: data.ContactOrganizerEmailRecipients
    ? data.ContactOrganizerEmailRecipients.map(({ Email }) => Email)
    : [],
  AreNotificationsAboutNewDonorsAndDonationsEnabled: data.AreNotificationsAboutNewDonorsAndDonationsEnabled,
  IsVisibleIntegrationSection: data.IsVisibleIntegrationSection,
  bloomerangFields: {
    isVisible: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.IsVisible ?? false,
    campaignFieldValues: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.CampaignFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.BloomerangFields.CampaignFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    campaignFieldValueID: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.CampaignFieldValueID ?? null,
    fundFieldValues: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.FundFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.BloomerangFields.FundFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    fundFieldValueID: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.FundFieldValueID ?? null,
    appealFieldValues: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.AppealFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.BloomerangFields.AppealFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    appealFieldValueID: data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.AppealFieldValueID ?? null,
    bloomerangFieldsUpdatedGroupName:
      data.IntegrationsOptionsSubsectionViewModel?.BloomerangFields?.BloomerangFieldsUpdatedGroupName ?? null,
  },
  littleGreenLightFields: {
    isVisible: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.IsVisible ?? false,
    campaignFieldValues: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.CampaignFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.LittleGreenLightFields.CampaignFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    campaignFieldValueID:
      data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.CampaignFieldValueID ?? null,
    fundFieldValues: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.FundFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.LittleGreenLightFields.FundFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    fundFieldValueID: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.FundFieldValueID ?? null,
    appealFieldValues: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.AppealFieldValues
      ? data.IntegrationsOptionsSubsectionViewModel.LittleGreenLightFields.AppealFieldValues.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        }))
      : [],
    appealFieldValueID: data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.AppealFieldValueID ?? null,
    littleGreenLightFieldsUpdatedGroupName:
      data.IntegrationsOptionsSubsectionViewModel?.LittleGreenLightFields?.LittleGreenLightFieldsUpdatedGroupName ??
      null,
  },
  organizationId: data.IntegrationsOptionsSubsectionViewModel?.OrganizationId ?? null,

  // permissions: {
  //   canAllowDonationsDirectlyToOrganization: data.Permissions.CanAllowDonationsDirectlyToOrganization,
  //   canChooseParentCampaign: data.Permissions.CanChooseParentCampaign,
  //   canEditAdministrators: data.Permissions.CanEditAdministrators,
  //   canEditAnonymityOfContributions: data.Permissions.CanEditAnonymityOfContributions,
  //   canEditChargingSettings: data.Permissions.CanEditChargingSettings,
  //   canEditCheckInfo: data.Permissions.CanEditCheckInfo,
  //   canEditCloseOnGoal: data.Permissions.CanEditCloseOnGoal,
  //   canEditCommonSettings: data.Permissions.CanEditCommonSettings,
  //   canEditContributionsDuration: data.Permissions.CanEditContributionsDuration,
  //   canEditCustomFields: data.Permissions.CanEditCustomFields,
  //   canEditCutOffDate: data.Permissions.CanEditCutOffDate,
  //   canEditDedicationOfContributions: data.Permissions.CanEditDedicationOfContributions,
  //   canEditDonationLimits: data.Permissions.CanEditDonationLimits,
  //   canEditOrganizerPromoCode: data.Permissions.CanEditOrganizerPromoCode,
  //   canEditPaymentTypes: data.Permissions.CanEditPaymentTypes,
  //   canEditPresetContributionAmounts: data.Permissions.CanEditPresetContributionAmounts,
  //   canEditPresets: data.Permissions.CanEditPresets,
  //   canEditRegistrationNotice: data.Permissions.CanEditRegistrationNotice,
  //   canEditTeamMemberRegistrationEndDate: data.Permissions.CanEditTeamMemberRegistrationEndDate,
  //   canEditTeamMemberResistartionStartDate: data.Permissions.CanEditTeamMemberResistartionStartDate,
  //   canEnableAttributes: data.Permissions.CanEnableAttributes,
  //   canTurnOnSpecialInstructions: data.Permissions.CanTurnOnSpecialInstructions,
  // },
  timezoneOptions: data.Timezones.map(({ ID, Name }) => ({
    value: ID,
    label: Name,
  })),
  timezone: data.TimezoneID,
  AllowRecurringDonations: data.AllowRecurringDonations,
  HasRecurringContributions: data.HasRecurringContributions,
  DefaultRecurrencePeriodID: data.DefaultRecurrencePeriodID,
  AllowDonorsChooseRecurringDonationFrequency: data.AllowDonorsChooseRecurringDonationFrequency,
  IsRecurringDonationUpsellEnabled: data.IsRecurringDonationUpsellEnabled,
  UpsellPercentage: data.UpsellPercentage,
  UpsellTitle: data.UpsellTitle,
  UpsellDescription: data.UpsellDescription,
  EnableAnonymousDonation: data.EnableAnonymousDonation,
  EnableHideEmail: data.EnableHideEmail,
});

/** main section */
export const loadDonationPageFirstFormData = (
  data: DonationPageFirstFormResponseType['CurrentSection'],
  currencyTypeID: number
): DonationPageMainSectionStoreType => ({
  CampaignName: data.CampaignName,
  shortDescription: data.ShortDescription,
  showCampaignHeadline: data.ShowCampaignHeadline,
  startCampaignAutomatically: data.IsStartCampaignAutomatically,
  DesiredStartDate: data.DesiredStartDate,
  DesiredStartTime: data.DesiredStartTime,
  // callToAction: {
  //   value: data.ContributionButtonTerminology.SelectedTerminology.IsDefault
  //     ? data.ContributionButtonTerminology.SelectedTerminology.ID
  //     : null,
  //   text: data.ContributionButtonTerminology.SelectedTerminology.Text,
  //   options: data.ContributionButtonTerminology.DefaultTerminologies.map(({ ID, Text }) => ({
  //     value: ID,
  //     label: Text,
  //   })),
  // },
  contributionPresets: {
    options: [],
    sort: data.IsContributionPresetsSortDesc ? SortDirectionEnum.DESC : SortDirectionEnum.ASC,
  },
  imageUri: data.Medias.Items.length === 0 ? null : data.Medias.Items[0].ImageUri,
  imageId: data.Medias.Items.length === 0 ? null : data.Medias.Items[0].ID,
  UseContributionAmountPresets: data.UseContributionAmountPresets,
  UseCauses: data.UseCauses,
  ContributionAmountPresets: (() => {
    if (!data.ContributionAmountPresets) {
      return [];
    }

    return data.IsContributionPresetsSortDesc
      ? reverse(data.ContributionAmountPresets)
      : data.ContributionAmountPresets;
  })(),
  contributionPresetsSort: data.IsContributionPresetsSortDesc ? SortDirectionEnum.DESC : SortDirectionEnum.ASC,
  // paymentMethods: data.PaymentMethods.filter(({ IsHidden }) => !IsHidden).map((item) => ({
  //   enabled: item.IsEnabled,
  //   selected: item.IsSelected,
  //   name: item.Name,
  //   value: item.Value,
  // })),
  currencies: data.Currencies
    ? data.Currencies.map(({ ID, Name, Symbol }) => ({
        label: Name,
        value: ID,
        symbol: Symbol.trim(),
      }))
    : [],
  currencySelected: currencyTypeID,
  isAddressRequired: data.IsAddressRequired,
  isPhoneNumberRequired: data.IsPhoneNumberRequired,
  // countries: data.Countries.map(({ Text, Value }) => ({
  //   value: Value,
  //   label: Text,
  // })),
  // check: {
  //   countryID: data.Check.CountryID,
  //   checkName: data.Check.CheckName,
  //   checkCity: data.Check.CheckCity,
  //   regionID: data.Check.RegionID,
  //   checkZipCode: data.Check.CheckZipCode,
  //   checkPayableTo: data.Check.CheckPayableTo,
  //   checkAttentionTo: data.Check.CheckAttentionTo,
  //   checkAddressLine1: data.Check.CheckAddressLine1,
  //   checkAddressLine2: data.Check.CheckAddressLine2,
  //   checkAddressLine3: data.Check.CheckAddressLine3,
  // },
  // UsRegions: data.UsRegions.map(({ DisplayName, ID }) => ({
  //   label: DisplayName,
  //   value: ID,
  // })),
  // CanadaRegions: data.CanadaRegions.map(({ DisplayName, ID }) => ({
  //   label: DisplayName,
  //   value: ID,
  // })),
  emailSubject: data.EmailSubject,
  emailTemplate: data.EmailTemplate,
  // textToGive: {
  //   enabled: data.TextToGiveViewModel.IsTextToGiveEnabled,
  //   keyword: data.TextToGiveViewModel.TextToGiveKeyword,
  // },
  friendlyUrl: data.FriendlyUrl,
  // defaultPricingViewModel: {
  //   showPromoCodeFeeChargingTypeCode: data.DefaultPricingViewModel.ShowPromoCodeFeeChargingTypeCode,
  //   promoCodeFeeChargingTypeCode: data.DefaultPricingViewModel.PromoCodeFeeChargingTypeCode,
  //   showDefaultPricingCard: data.DefaultPricingViewModel.ShowDefaultPricingCard,
  //   isTipPricingAvailable: data.DefaultPricingViewModel.IsTipPricingAvailable,
  //   serviceFeePercent: data.DefaultPricingViewModel.ServiceFeePercent,
  //   pricingTypeCode: data.DefaultPricingViewModel.PricingTypeCode,
  //   DefaultPricingServiceFeeChargingTypeID: data.DefaultPricingViewModel.DefaultPricingServiceFeeChargingTypeID,
  //   isConvenienceFeeEnabled: data.DefaultPricingViewModel.IsConvenienceFeeEnabled,
  //   convenienceFeeAmount: data.DefaultPricingViewModel.ConvenienceFeeAmount,
  //   serviceFeeRate: data.DefaultPricingViewModel.ServiceFeeRate,
  //   paymentProcessorTypeCodes: data.DefaultPricingViewModel.PaymentProcessorTypeCodes,
  // },
  permissions: {
    canAddImage: data.Permissions.CanAddImage,
    canEditDonationLimits: data.Permissions.CanEditDonationLimits,
    canEditCampaignPricing: data.Permissions.CanEditCampaignPricing,
    canEditChargingSettings: data.Permissions.CanEditChargingSettings,
    canEditCheckInfo: data.Permissions.CanEditCheckInfo,
    canEditCommonSettings: data.Permissions.CanEditCommonSettings,
    canEditCurrencies: data.Permissions.CanEditCurrencies,
    canEditCutOffDate: data.Permissions.CanEditCutOffDate,
    canEditDesiredStartDate: data.Permissions.CanEditDesiredStartDate,
    canEditPaymentTypes: data.Permissions.CanEditPaymentTypes,
    canEditPresetContributionAmounts: data.Permissions.CanEditPresetContributionAmounts,
    canEditPresets: data.Permissions.CanEditPresets,
  },
  DonationPageDisplayingConfigurationModel: data.DonationPageDisplayingConfigurationModel,
  cause: {
    value: data.CauseTerminology.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.CauseTerminology.SelectedTerminology.ID,
    options: data.CauseTerminology.DefaultTerminologies.map(({ ID, Plural, Singular }) => ({
      value: ID,
      label: Plural,
      singular: Singular,
    })),
    plural: data.CauseTerminology.SelectedTerminology.IsCampaignSpecific
      ? data.CauseTerminology.SelectedTerminology.Plural
      : null,
    singular: data.CauseTerminology.SelectedTerminology.IsCampaignSpecific
      ? data.CauseTerminology.SelectedTerminology.Singular
      : null,
    article: data.CauseTerminology.SelectedTerminology.Article.toLowerCase() === 'a' ? 'a' : 'an',
  },
  terminology: {
    value: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.ID,
    options: data.TerminologiesViewModel.SupporterTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    plural: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Singular,
    article:
      data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
  },
  contribution: {
    options: data.TerminologiesViewModel.ContributionTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    value: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.ID,
    article:
      data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
    verb: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Verb,
    plural: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Singular,
  },
  FundsList: [],
  FundsListLoaded: false,
  CurrencyTypeID: data.CurrencyTypeID,
});

/* eslint-disable react/destructuring-assignment */

/* preferences sections */
export const LoadDonationPageCheckoutSectionData = (
  data: DonationPageSecondFormResponseType['CurrentSection'],
  currencyTypeID: number,
  currencyType?: string
): DonationPageCheckoutSectionStoreType => ({
  currencies: data.Currencies
    ? data.Currencies.map(({ ID, Name, Symbol }) => ({
        label: Name,
        value: ID,
        symbol: Symbol.trim(),
      }))
    : [],
  permissions: {
    canAddImage: data.Permissions.CanAddImage,
    canEditDonationLimits: data.Permissions.CanEditDonationLimits,
    canEditCampaignPricing: data.Permissions.CanEditCampaignPricing,
    canEditChargingSettings: data.Permissions.CanEditChargingSettings,
    canEditCheckInfo: data.Permissions.CanEditCheckInfo,
    canEditCommonSettings: data.Permissions.CanEditCommonSettings,
    canEditCurrencies: data.Permissions.CanEditCurrencies,
    canEditCutOffDate: data.Permissions.CanEditCutOffDate,
    canEditDesiredStartDate: data.Permissions.CanEditDesiredStartDate,
    canEditPaymentTypes: data.Permissions.CanEditPaymentTypes,
    canEditPresetContributionAmounts: data.Permissions.CanEditPresetContributionAmounts,
    canEditPresets: data.Permissions.CanEditPresets,
  },
  paymentMethods: data.PaymentMethods.map((item) => ({
    enabled: item.IsEnabled,
    selected: item.IsSelected,
    hidden: item.IsHidden,
    name: item.Name,
    value: item.Value,
  })),
  terminology: {
    value: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.ID,
    options: data.TerminologiesViewModel.SupporterTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    plural: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Singular,
    article:
      data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
  },
  projectTerminology: {
    value: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.ID,
    options: data.TerminologiesViewModel.SupporterTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    plural: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Singular,
    article:
      data.TerminologiesViewModel.SupporterTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
  },
  check: {
    countryID: data.Check.CountryID,
    checkName: data.Check.CheckName,
    checkCity: data.Check.CheckCity,
    regionID: data.Check.RegionID,
    checkZipCode: data.Check.CheckZipCode,
    checkPayableTo: data.Check.CheckPayableTo,
    checkAttentionTo: data.Check.CheckAttentionTo,
    checkAddressLine1: data.Check.CheckAddressLine1,
    checkAddressLine2: data.Check.CheckAddressLine2,
    checkAddressLine3: data.Check.CheckAddressLine3,
  },
  countries: data.Countries.map(({ Text, Value }) => ({
    value: Value,
    label: Text,
  })),
  UsRegions: data.UsRegions.map(({ DisplayName, ID }) => ({
    label: DisplayName,
    value: ID,
  })),
  CanadaRegions: data.CanadaRegions.map(({ DisplayName, ID }) => ({
    label: DisplayName,
    value: ID,
  })),
  contribution: {
    options: data.TerminologiesViewModel.ContributionTerminologyModel.DefaultTerminologies.map(
      ({ ID, Plural, Singular }) => ({
        value: ID,
        label: Plural,
        singular: Singular,
      })
    ),
    value: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.IsCampaignSpecific
      ? null
      : data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.ID,
    article:
      data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Article.toLowerCase() === 'a'
        ? 'a'
        : 'an',
    verb: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Verb,
    plural: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Plural,
    singular: data.TerminologiesViewModel.ContributionTerminologyModel.SelectedTerminology.Singular,
  },
  currencySelected: {
    id: currencyTypeID,
    shortName: currencyType || getCurrencySymbolById(currencyTypeID) || '|',
  },
  emailSubject: data.EmailSubject,
  emailTemplate: data.EmailTemplate,
  areFixedContributionsAllowed: data.AreFixedContributionsAllowed,
  allowDonationDedication: data.AllowDonationDedication,
  useCustomMinMaxContributionAmount: data.UseCustomMinMaxContributionAmount,
  minimumContributionAmount: data.MinimumContributionAmount,
  maximumContributionAmount: data.MaximumContributionAmount,
  isAddressRequired: data.IsAddressRequired,
  isPhoneNumberRequired: data.IsPhoneNumberRequired,
  CustomFields: {
    BasicFields: data.CustomFields.BasicFields,
    CustomFields: data.CustomFields.CustomFields.map(
      ({ ID, Name, IsRequired, CustomFieldTypeID, HasOtherOption, Options, HasNonEmptyValues }) => ({
        ID,
        IsRequired,
        Name,
        CustomFieldTypeID,
        HasOtherOption,
        HasNonEmptyValues,
        Options: Options.map(({ ID, Name }) => ({
          ID,
          Name,
        })),
      })
    ),
    Types: data.CustomFields.Types,
  },
  isProPricingEnabled: data.IsProPricingEnabled,
  defaultPricingViewModel: {
    showPromoCodeFeeChargingTypeCode: data.DefaultPricingViewModel.ShowPromoCodeFeeChargingTypeCode,
    promoCodeFeeChargingTypeCode: data.DefaultPricingViewModel.PromoCodeFeeChargingTypeCode,
    showDefaultPricingCard: data.DefaultPricingViewModel.ShowDefaultPricingCard,
    isTipPricingAvailable: data.DefaultPricingViewModel.IsTipPricingAvailable,
    serviceFeePercent: data.DefaultPricingViewModel.ServiceFeePercent,
    pricingTypeCode: data.DefaultPricingViewModel.PricingTypeCode,
    DefaultPricingServiceFeeChargingTypeID: data.DefaultPricingViewModel.DefaultPricingServiceFeeChargingTypeID,
    DefaultPricingProcessingFeeChargingTypeID: data.DefaultPricingViewModel.DefaultPricingProcessingFeeChargingTypeID,
    isConvenienceFeeEnabled: data.DefaultPricingViewModel.IsConvenienceFeeEnabled,
    ConvenienceFeeAmountType: data.DefaultPricingViewModel.ConvenienceFeeAmountType || 2, // FeeTypes
    IsPercentageConvenienceFeeAvailable: data.DefaultPricingViewModel.IsPercentageConvenienceFeeAvailable,
    ConvenienceFeeAmount: data.DefaultPricingViewModel.ConvenienceFeeAmount,
    serviceFeeRate: data.DefaultPricingViewModel.ServiceFeeRate,
    paymentProcessorTypeCodes: data.DefaultPricingViewModel.PaymentProcessorTypeCodes,
    processingFeesChargedToSupporter: data.DefaultPricingViewModel.ProcessingFeesChargedToSupporter,
    showProcessingCard: data.DefaultPricingViewModel.CanChargeProcessingFeeToSupporter,
  },
  PlusSubscriptionPricingViewModel: {
    isVisible: data.PlusSubscriptionPricingViewModel.IsVisible,
    showPlusSubscriptionPricingCard: data.PlusSubscriptionPricingViewModel.ShowPlusSubscriptionPricingCard,
    pricingPlanName: data.PlusSubscriptionPricingViewModel.PricingPlanName,
    showProcessingCard: data.PlusSubscriptionPricingViewModel.CanChargeProcessingFeeToSupporter,
    processingFeesChargedToSupporter: data.PlusSubscriptionPricingViewModel.ProcessingFeesChargedToSupporter,
    showFeePercent: data.PlusSubscriptionPricingViewModel.ShowFeePercent,
    serviceFeeChangedGroupName: data.PlusSubscriptionPricingViewModel.ServiceFeeChangedGroupName,
    serviceFeePercent: data.PlusSubscriptionPricingViewModel.ServiceFeePercent,
    serviceFeeRate: data.PlusSubscriptionPricingViewModel.ServiceFeeRate,
    DefaultPricingServiceFeeChargingTypeID:
      data.PlusSubscriptionPricingViewModel.DefaultPricingServiceFeeChargingTypeID,
    DefaultPricingProcessingFeeChargingTypeID:
      data.PlusSubscriptionPricingViewModel.DefaultPricingProcessingFeeChargingTypeID,
    isConvenienceFeeEnabled: data.PlusSubscriptionPricingViewModel.IsConvenienceFeeEnabled,
    ConvenienceFeeAmountType: data.PlusSubscriptionPricingViewModel.ConvenienceFeeAmountType || 2, // FeeTypes
    IsPercentageConvenienceFeeAvailable: data.PlusSubscriptionPricingViewModel.IsPercentageConvenienceFeeAvailable,
    ConvenienceFeeAmount: data.PlusSubscriptionPricingViewModel.ConvenienceFeeAmount,
    paymentProcessorTypeCodes: data.PlusSubscriptionPricingViewModel.PaymentProcessorTypeCodes,
  },
  textToGive: {
    enabled: data.TextToGiveViewModel.IsTextToGiveEnabled,
    keyword: data.TextToGiveViewModel.TextToGiveKeyword,
    internationalEnabled: data.TextToGiveViewModel.IsInternationalTextToGiveEnabled,
  },
  enableMarketingEmailsOption: data.EnableMarketingEmailsOption,
  enableMarketingEmailsOptionLabel: data.EnableMarketingEmailsOptionLabel,
  isInTestMode: data.IsInTestMode,
});
